import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HardDrive } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { speicherProzent, speicherText, useSpeicher } from '@/lib/speicher'

/**
 * AP-11-E (E4) — „Der Speicher ist zu 84 % belegt": Hinweis im Rahmen der
 * Anwendung, sobald 80 % erreicht sind. Nur für die, die den Speicher einsehen
 * dürfen — wer nichts daran ändern kann, soll nicht bei jeder Seite gewarnt werden.
 */
export function SpeicherHinweis() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const ort = useLocation()
  const user = useAuthStore((s) => s.user)
  const darf = erfuelltRecht('core.storage.read', user?.permissions)
  const stand = useSpeicher((s) => s.stand)
  const lade = useSpeicher((s) => s.lade)
  const leere = useSpeicher((s) => s.leere)

  useEffect(() => {
    if (darf) void lade()
    else leere()
    // Neu bei Wechsel des Mandanten — der Stand gehört dem Betrieb, nicht der Person.
  }, [darf, user?.tenantId, lade, leere])

  // Auf der Seite selbst steht derselbe Stand ausführlicher.
  if (!darf || !stand || stand.level === 'OK' || stand.limitBytes === null) return null
  if (ort.pathname === '/settings/storage') return null

  const voll = stand.level === 'FULL'
  return (
    <div
      className={`flex flex-wrap items-center gap-3 rounded-md border px-4 py-3 text-sm text-foreground ${
        voll ? 'border-destructive/30 bg-destructive/10' : 'border-amber-500/30 bg-amber-500/10'
      }`}
    >
      <HardDrive size={18} className={`shrink-0 ${voll ? 'text-destructive' : 'text-amber-600 dark:text-amber-400'}`} />
      <span className="flex-1">
        {t(voll ? 'storage.hinweis.voll' : 'storage.hinweis.knapp', {
          prozent: speicherProzent(stand),
          belegt: speicherText(stand.usedBytes),
          grenze: speicherText(stand.limitBytes),
        })}
      </span>
      <button
        type="button"
        onClick={() => navigate('/settings/storage')}
        className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        {t('storage.hinweis.ansehen')}
      </button>
    </div>
  )
}
